import { useEffect, useRef } from 'react';
import { useSocketStore } from '../../stores/useSocketStore';
import { useLoadingStore } from '../../stores/useLoadingStore';

// =========================
// CONFIG
// =========================

const PING_INTERVAL_MS = 10000;
const PING_TIMEOUT_MS = 5000;

const MAX_SAMPLES = 5;

const GOOD_MAX_LATENCY = 300;
const MEDIUM_MAX_LATENCY = 1000;

// =========================
// QUALITY
// =========================

function getConnectionQuality(latency) {

    if (latency < GOOD_MAX_LATENCY) {
        return 'good';
    }

    if (latency < MEDIUM_MAX_LATENCY) {
        return 'medium';
    }

    return 'poor';

}

// =========================
// HOOK
// =========================

export default function useConnectionHealth() {

    const socket =
        useSocketStore(
            state => state.socket
        );

    const samplesRef = useRef([]);

    const hasConnectedRef = useRef(false);

    useEffect(() => {

        if (!socket) {
            return;
        }

        let intervalId = null;

        // =========================
        // ATUALIZA LATÊNCIA
        // =========================

        const updateLatency = latency => {

            const samples = samplesRef.current;

            samples.push(latency);

            if (
                samples.length >
                MAX_SAMPLES
            ) {
                samples.shift();
            }

            const average =
                samples.reduce(
                    (total, value) =>
                        total + value,
                    0
                ) / samples.length;

            const normalizedLatency =
                Math.round(average);

            const quality =
                getConnectionQuality(
                    normalizedLatency
                );

            useSocketStore.setState({
                connectionLatency:
                    normalizedLatency,

                connectionQuality:
                    quality
            });

        };

        // =========================
        // PING
        // =========================

        const checkConnection = () => {

             const {
                setLoadingFooter,
                setLoadingFooterTxt
            } = useLoadingStore.getState();

            if (!socket.connected) {

                useSocketStore.setState({
                    connectionQuality: 'offline',
                    connectionLatency: null
                });

                return;
            }

            const startedAt = performance.now();
            
            // =========================
            // PRIMEIRA CONEXÃO
            // =========================

            if (!hasConnectedRef.current) {

                setLoadingFooter(true);

                setLoadingFooterTxt(
                    'Estabelecendo conexão...'
                );

            }

            socket
                .timeout(PING_TIMEOUT_MS)
                .emit(
                    'connection:ping',
                    {},
                    err => {

                        if (err) {

                            useSocketStore.setState({
                                connectionQuality: 'poor'
                            });

                            return;
                        }

                        const latency =
                            performance.now() -
                            startedAt;

                        updateLatency(latency);

                        hasConnectedRef.current = true;

                        useSocketStore.setState({
                            connectionReady: true
                        });

                    }
                );

        };

        // =========================
        // CONNECT
        // =========================

        const handleConnect = () => {

            samplesRef.current = [];

            useSocketStore.setState({
                connectionReady: false
            });

            checkConnection();

        };

        // =========================
        // DISCONNECT
        // =========================

        const handleDisconnect = () => {

            samplesRef.current = [];

            hasConnectedRef.current = false;

            useSocketStore.setState({
                connectionQuality: 'offline',
                connectionLatency: null,
                connectionReady: false
            });

        };

        // =========================
        // LISTENERS
        // =========================

        socket.on( 'connect', handleConnect);

        socket.on('disconnect', handleDisconnect);

        // =========================
        // JÁ CONECTADO
        // =========================

        if (socket.connected) {
            handleConnect();
        } else {

            useSocketStore.setState({
                connectionQuality: 'offline',
                connectionLatency: null
            });

        }

        // =========================
        // INTERVAL
        // =========================

        intervalId =
            setInterval(
                checkConnection,
                PING_INTERVAL_MS
            );

        // =========================
        // CLEANUP
        // =========================

        return () => {

            clearInterval(intervalId);

            socket.off('connect', handleConnect);

            socket.off('disconnect', handleDisconnect);

            samplesRef.current = [];

        };

    }, [socket]);

}